import { ReactElement, useEffect, useState } from 'react';
import { Input, Modal, ModalFooter } from '@sc/shared-ui';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { useNavigate } from '@tanstack/react-router';
import { CompanyUserRole } from '@sc/graphql';
import { GQL_MUTATION_DELETE_ME } from '../utility/queries';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import { useAuthenticated } from '../providers/authenticated';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';

const CONFIRM_PHRASE = 'DELETE';

export const DeleteAccountModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement => {
  const { user } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();
  const { setIsAuthenticated } = useAuthenticated();
  const client = useApolloClient();
  const navigate = useNavigate();

  const [confirm, setConfirm] = useState('');
  const isAccountOwner = user.role === CompanyUserRole.accountOwner;

  const [deleteMe, { loading: isDeleting }] = useMutation(
    GQL_MUTATION_DELETE_ME
  );

  useEffect(() => {
    if (open) track('Account Deletion Started');
  }, [open]);

  const handleClose = (): void => {
    setConfirm('');

    onClose();
  };

  const handleDelete = (): void => {
    void deleteMe({
      onCompleted: () => {
        track(
          'Account Deleted',
          { role: user.role, deletedCompany: isAccountOwner },
          { sendImmediately: true }
        );

        notify.success('Account deleted');

        client.defaultContext.accessToken = undefined;

        void client.clearStore().then(() => {
          sessionStorage.clear();

          localStorage.clear();

          setIsAuthenticated(null);

          void navigate({ to: '/login' });
        });
      },
      onError,
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Delete account"
      icon="critical"
      dialogPanelClassname="w-[520px]"
      loading={isDeleting}
    >
      <div className="p-5 space-y-4">
        {isAccountOwner ? (
          <p>
            You are the account owner. Deleting your account will also delete
            your company and remove access for every member. This action is
            reversible for 30 days — signing back in within that window restores
            the company. After 30 days, all data is permanently deleted.
          </p>
        ) : (
          <p>
            Your account will be marked for deletion. You can sign back in
            within 30 days to restore access. After 30 days, all your data is
            permanently deleted.
          </p>
        )}
        <p className="text-text-low-priority text-sm">
          Type <span className="font-semibold">{CONFIRM_PHRASE}</span> to
          confirm.
        </p>
        <Input
          label="Confirmation"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      <ModalFooter
        isDelete
        onClose={handleClose}
        submitText="Delete account"
        buttonsDisabled={isDeleting || confirm !== CONFIRM_PHRASE}
        onSubmit={handleDelete}
      />
    </Modal>
  );
};
